"use client";

import { useRef, useLayoutEffect, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import IlFeatured from "@/assets/videos/featured.mp4";
import icCheck from "@/assets/icons/ic_check.svg";
import ImgFeatured from "@/assets/images/img_featured.webp";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useTranslation } from "react-i18next";

interface FeatureSectionProps extends React.HTMLAttributes<HTMLElement> {}

function FeatureSection({ className, ...props }: FeatureSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const card1Ref = useRef<HTMLDivElement>(null);
  const card1VideoRef = useRef<HTMLVideoElement>(null);
  const card1ContentRef = useRef<HTMLDivElement>(null);
  const card2Ref = useRef<HTMLDivElement>(null);
  const card2ContentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const features = t("features.stats", { returnObjects: true }) as string[];

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  
  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useGSAP(() => {
    const ctx = gsap.context(() => {
      // Animation for first card container - slide from left with fade
      if (card1Ref.current) {
        gsap.fromTo(
          card1Ref.current,
          {
            y: 50,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card1Ref.current,
              start: "top 85%",
              end: "top 40%",
              scrub: 1,
            },
          }
        );
      }

      // Animation for card1 image - scale up with fade
      if (card1VideoRef.current) {
        gsap.fromTo(
          card1VideoRef.current,
          {
            scale: 0.96,
            opacity: 0,
          },
          {
            scale: 1,
            opacity: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card1Ref.current,
              start: "top 75%",
              end: "top 35%",
              scrub: 1,
            },
          }
        );
      }

      // Animation for card1 content - stagger children from right
      if (card1ContentRef.current) {
        gsap.set(card1ContentRef, { opacity: 0 });

        gsap.fromTo(
          card1ContentRef,
          { opacity: 0 },
          {
            opacity: 1,
            ease: "power4.out",
            stagger: {
              each: 0.1,
              from: "start",
            },
            scrollTrigger: {
              trigger: card1Ref.current,
              start: "top 70%",
              end: "top center",
              scrub: 1,
            },
          }
        );
      }

      // Animation for second card - slide from right with fade
      if (card2Ref.current) {
        gsap.fromTo(
          card2Ref.current,
          {
            y: 50,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card2Ref.current,
              start: "top 85%",
              end: "top 40%",
              scrub: 1,
            },
          }
        );

        if(card2ContentRef.current){
          gsap.set(card2ContentRef, { opacity: 0 });
          gsap.fromTo(
            card1ContentRef,
            { opacity: 0 },
            {
              opacity: 1,
              ease: "power4.out",
              stagger: {
                each: 0.1,
                from: "start",
              },
              scrollTrigger: {
                trigger: card2Ref.current,
                start: "top 85%",
                end: "top 40%",
                scrub: 1,
              },
            }
          );
        }
      }
    }, sectionRef);

    return () => ctx.revert();
  });

  return (
    <section
      ref={sectionRef}
      className={cn(
        "px-4 py-12 space-y-12",
        "md:px-6 md:py-24 md:space-y-24",
        "2k:py-32 2k:space-y-32",
        "4k:py-48 4k:space-y-48",
        className
      )}
      {...props}
    >
      <div 
        ref={card1Ref} 
        className={cn(
          "flex flex-col md:flex-row items-center bg-lavender-dark rounded-md py-4",
          "md:rounded-lg md:py-6",
          "2k:py-12 2k:rounded-xl",
          "4k:py-18 4k:rounded-4xl"
        )}>
        {/* Image */}
        <div className="flex-1 w-full justify-items-center">
          <video
            autoPlay
            muted
            loop
            playsInline
            ref={card1VideoRef}
            src={IlFeatured}
            className={cn(
              "w-[280px] h-60 object-contain translate-y-6 mix-blend-multiply",
              "md:w-[475px] md:h-[400px] md:translate-y-0",
              "2k:w-[900px] 2k:h-[600px] md:translate-y-0",
              "4k:w-[1225px] 4k:h-[1000px] md:translate-y-0"
            )}
          />
        </div>

        {/* Content */}
        <div ref={card1ContentRef} className="flex-1 w-full px-10 md:px-4 md:p-6 lg:p-8">
          <h3 className={cn(isMobile ? "font-mobile-large-headline" : "font-h2","mb-6 md:mb-6 2k:mb-12 4k:mb-28")}>
            {t("features.whyVideo")}
          </h3>
          <ul className="space-y-4 2k:space-y-6 4k:space-y-10">
            {features.map((feature, index) => (
              <li key={index} className="flex md:items-center gap-2 md:gap-3">
                <img 
                  src={icCheck} 
                  alt="Check icon" 
                  className={cn(
                    "mt-1 md:mt-0 size-6",
                    "2k:w-10 2k:h-10",
                    "4k:w-15 4k:h-15"
                  )} 
                />
                <span className={cn(
                  "text-base font-bold",
                  "md:text-xl",
                  "2k:text-3xl",
                  "4k:text-5xl"
                )}>
                  {feature}
                </span>
              </li>
            ))}
          </ul>

          <p className={cn(isMobile ? "font-[16px]" : "font-body"," mt-4 md:mt-8 2k:mt-12 4k:mt-18")}>
            {t("features.source")}
          </p>

          <div className={cn(
            "flex justify-center md:justify-start mt-5 mb-8",
            "md:mt-4 md:mb-0",
            "2k:mt-8 2k:mb-0",
            "4k:mt-10 4k:mb-0",
          )}>
            <Button
              onClick={() => window.open("https://www.hemnet.se/artiklar/guider/2024/12/19/alla-vinner-med-video-i-bostadsannonsen", "_blank")}
              variant="outline"
              className={cn(
                "rounded-sm border border-black bg-transparent hover:bg-white/20 font-medium text-base py-6 md:py-6 px-6 cursor-pointer",
                "md:text-[20px] md:py-5",
                "2k:rounded-lg 2k:text-3xl 2k:py-8 2k:px-10",
                "4k:rounded-xl 4k:text-5xl 4k:py-12 4k:px-12"
              )}
              asChild
            >
              <p>{t("features.readMore")}</p>
            </Button>
          </div>
        </div>
      </div>

      <div ref={card2Ref} className="flex flex-col md:flex-row items-center">
        {/* Image */}
        <div className="flex-1 w-full justify-items-center">
          <img
            src={ImgFeatured}
            alt="Modern living room"
            style={{ aspectRatio: '453 / 302' }}
            className={cn(
              "object-cover 2k:h-150 4k:h-200 overflow-hidden rounded-sm scale-[0.82]",
              "md:rounded-md md:scale-[0.8]",
              "2k:rounded-lg 2k:scale-[0.7]",
              "4k:rounded-4xl"
            )}
          />
        </div>

        {/* Content */}
        <div className="flex-1 w-full px-8 py-4 md:p-6 lg:p-8">
          <p ref={card2ContentRef} className="font-paragraph text-rose-dark">
            {t("features.description")}
          </p>
        </div>
      </div>
    </section>
  );
}

export { FeatureSection };
